import { BaseService, PaginatedResult } from "./base.service";

// DTOs for Notes API
export interface MediaDto {
  id?: string;
  url: string;
  type: string;
  filename: string;
}

export interface NoteDto {
  id?: string;
  memberId: string;
  title: string;
  content: string;
  attachments?: MediaDto[];
  type: 'personal' | 'family';
}

export interface CreateNoteDto {
  memberId: string;
  title: string;
  content: string;
  attachments?: MediaDto[];
  type: 'personal' | 'family';
}

export interface UpdateNoteDto {
  title?: string;
  content?: string;
  attachments?: MediaDto[];
  type?: 'personal' | 'family';
}

export interface NoteResponseDto {
  id: string;
  memberId: string;
  ownerId: string;
  title: string;
  content: string;
  attachments: MediaDto[];
  type: 'personal' | 'family';
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  unreadCommentsCount?: number;
  unread?: boolean;
  member?: {
    firstName: string;
    lastName: string;
    avatar?: string | null;
  };
}

export interface NotesQueryParams {
  memberId?: string;
  type?: 'personal' | 'family';
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export class NotesService extends BaseService {
  constructor(memberId: string) {
    super(`/members/${memberId}/notes`);
  }

  // Get all notes with advanced filtering (POST)
  async getNotes(filter?: any): Promise<PaginatedResult<NoteResponseDto>> {
    return await this.post<PaginatedResult<NoteResponseDto>>('/getAll', filter || {});
  }

  // Get a single note by ID
  async getNote(id: string): Promise<NoteResponseDto> {
    return await this.get<NoteResponseDto>(`/${id}`);
  }

  // Create a new note
  async createNote(note: CreateNoteDto): Promise<NoteResponseDto> {
    return await this.post<NoteResponseDto>('', note);
  }

  // Update an existing note
  async updateNote(id: string, note: UpdateNoteDto): Promise<NoteResponseDto> {
    return await this.put<NoteResponseDto>(`/${id}`, note);
  }

  // Delete a note
  async deleteNote(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }

  // Add a comment to a note
  async addComment(noteId: string, content: string): Promise<any> {
    return await this.post<any>(`/${noteId}/comment`, { content });
  }

  // Update a comment on a note
  async updateComment(noteId: string, commentId: string, content: string): Promise<any> {
    return await this.put<any>(`/${noteId}/comment/${commentId}`, { content });
  }

  // Delete a comment on a note
  async deleteComment(noteId: string, commentId: string): Promise<any> {
    return await this.delete(`/${noteId}/comment/${commentId}`);
  }

  // Mark a note as read
  async markAsReadNote(noteId: string): Promise<any> {
    return await this.put<any>(`/${noteId}/mark-as-read`, {});
  }

  // Get family unread notes count
  async getFamilyUnreadCount(): Promise<number> {
    const res = await this.get<{ count: number }>(`/unread-count`);
    return res?.count || 0;
  }
}

